import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Alert, CircularProgress, Avatar, MenuItem, TextField,
  LinearProgress, InputAdornment,
} from '@mui/material';
import { WorkspacePremium, TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const nMonthsAgo = (n: number) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

const SOURCE_CFG: Record<string, { label: string; emoji: string; color: string }> = {
  referral:            { label: 'Employee Referral',   emoji: '👥', color: '#2e7d32' },
  linkedin:            { label: 'LinkedIn',             emoji: '💼', color: '#0077b5' },
  job_board:           { label: 'Job Board',            emoji: '📋', color: '#1565c0' },
  agency:              { label: 'Agency',               emoji: '🏢', color: '#6a1b9a' },
  direct_application:  { label: 'Direct Application',  emoji: '📩', color: '#e65100' },
  career_fair:         { label: 'Career Fair',          emoji: '🎪', color: '#00838f' },
  other:               { label: 'Other',                emoji: '📄', color: '#546e7a' },
};

const STAGE_COLORS: Record<string, string> = {
  applied:       '#78909c',
  screening:     '#1565c0',
  interviewing:  '#6a1b9a',
  final_review:  '#e65100',
  offer_pending: '#f57f17',
  offer_accepted:'#2e7d32',
  hired:         '#1b5e20',
  rejected:      '#b71c1c',
  withdrawn:     '#757575',
  offer_declined:'#c62828',
};

const pct = (v: any) => `${parseFloat(v || 0).toFixed(1)}%`;
const num = (v: any) => parseInt(v || 0).toLocaleString();
const days = (v: any) => v ? `${Math.round(parseFloat(v))}d` : '—';

interface TrendArrow { value: number; prev?: number }
function Trend({ value, prev, suffix = '' }: { value: number; prev?: number; suffix?: string }) {
  if (prev === undefined) return <Typography variant="body2">{value}{suffix}</Typography>;
  const diff = value - prev;
  if (Math.abs(diff) < 0.5) return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}><TrendingFlat fontSize="small" sx={{ color: '#78909c' }} /><Typography variant="caption">{value}{suffix}</Typography></Box>;
  const up = diff > 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {up ? <TrendingUp fontSize="small" sx={{ color: '#2e7d32' }} /> : <TrendingDown fontSize="small" sx={{ color: '#b71c1c' }} />}
      <Typography variant="caption" color={up ? '#2e7d32' : '#b71c1c'}>{value}{suffix} ({up ? '+' : ''}{diff.toFixed(1)}{suffix})</Typography>
    </Box>
  );
}

export default function RecruitmentAnalytics() {
  const [tab, setTab]       = useState(0);
  const [data, setData]     = useState<any>(null);
  const [pipeline, setPipeline] = useState<any[]>([]);
  const [depts, setDepts]   = useState<any[]>([]);
  const [from, setFrom]     = useState(nMonthsAgo(6));
  const [to, setTo]         = useState(today());
  const [deptFilter, setDeptFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to });
      if (deptFilter) params.set('departmentId', deptFilter);

      const [dash, pipe, deptList] = await Promise.all([
        apiFetch(`/api/recruitment-analytics/dashboard?${params}`),
        apiFetch(`/api/recruitment-analytics/pipeline${deptFilter ? `?departmentId=${deptFilter}` : ''}`),
        apiFetch('/api/departments'),
      ]);
      setData(dash);
      setPipeline(pipe ?? []);
      setDepts(Array.isArray(deptList) ? deptList : (deptList.data ?? []));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, [from, to, deptFilter]);

  useEffect(() => { load(); }, [load]);

  const ov = data?.overview;
  const funnel = (data?.funnel ?? []).filter((f: any) => f.isFunnel);
  const funnelMax = funnel.length ? Math.max(...funnel.map((f: any) => f.count), 1) : 1;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: '#6a1b9a', width: 48, height: 48 }}><WorkspacePremium /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Recruitment Analytics</Typography>
          <Typography variant="body2" color="text.secondary">
            Pipeline funnel · time-to-hire · source effectiveness · offer analysis
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Date + dept filter */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }}
          value={from} onChange={e => setFrom(e.target.value)} />
        <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }}
          value={to} onChange={e => setTo(e.target.value)} />
        <TextField select size="small" label="Department" value={deptFilter}
          onChange={e => setDeptFilter(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">All Departments</MenuItem>
          {depts.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
        {[
          ['Last 3 months', 3], ['Last 6 months', 6], ['Last 12 months', 12],
        ].map(([label, n]) => (
          <Chip key={label as string} label={label} size="small" clickable
            onClick={() => { setFrom(nMonthsAgo(n as number)); setTo(today()); }}
            sx={{ bgcolor: from === nMonthsAgo(n as number) ? '#6a1b9a' : undefined, color: from === nMonthsAgo(n as number) ? '#fff' : undefined }} />
        ))}
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* KPI cards */}
          {ov && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Total Applications',   value: num(ov.totalApplications),               color: '#546e7a' },
                { label: 'Hired',                value: num(ov.hired),                           color: '#2e7d32' },
                { label: 'Active Candidates',    value: num(ov.active),                          color: '#1565c0' },
                { label: 'Conversion Rate',      value: pct(ov.conversionRate),                  color: '#6a1b9a' },
                { label: 'Offer Acceptance',     value: pct(ov.acceptanceRate),                  color: '#2e7d32' },
                { label: 'Open Requisitions',    value: num(ov.openReqs),                        color: '#e65100' },
                { label: 'Avg Days to Fill',     value: days(ov.avgDaysToFill),                  color: '#f57f17' },
                { label: 'Total Offers',         value: num(ov.totalOffers),                     color: '#546e7a' },
              ].map(s => (
                <Grid size={{ xs: 6, sm: 3, md: 1.5 }} key={s.label}>
                  <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                    <Typography variant="h5" fontWeight={700} color={s.color}>{s.value}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, display: 'block' }}>{s.label}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Pipeline Funnel" />
            <Tab label="Time to Hire" />
            <Tab label="Source Effectiveness" />
            <Tab label="Offer Analysis" />
            <Tab label="By Department" />
            <Tab label="Monthly Trend" />
            <Tab label="Active Pipeline" />
            <Tab label="Interviews" />
          </Tabs>

          {/* ── Funnel ── */}
          {tab === 0 && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Hiring Funnel</Typography>
                  {funnel.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 2 }}>No application data for selected period.</Typography>
                  ) : (
                    funnel.map((f: any, idx: number) => {
                      const color = STAGE_COLORS[f.stage] ?? '#546e7a';
                      const prev = idx > 0 ? funnel[idx - 1]?.count : null;
                      const dropPct = prev && prev > 0 ? Math.round((1 - f.count / prev) * 100) : null;
                      return (
                        <Box key={f.stage} sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                            <Typography variant="body2">{f.label}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {dropPct !== null && dropPct > 0 && (
                                <Chip size="small" label={`-${dropPct}%`} sx={{ fontSize: '0.6rem', bgcolor: '#ffebee', color: '#b71c1c' }} />
                              )}
                              <Typography variant="body2" fontWeight={700} color={color}>{f.count}</Typography>
                            </Box>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.round((f.count / funnelMax) * 100)}
                            sx={{ height: 20, borderRadius: 2,
                              '& .MuiLinearProgress-bar': { bgcolor: color },
                              bgcolor: color + '20' }} />
                        </Box>
                      );
                    })
                  )}
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>All Dispositions</Typography>
                  {(data?.funnel ?? []).map((f: any) => (
                    <Box key={f.stage} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f5f5f5' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STAGE_COLORS[f.stage] ?? '#546e7a' }} />
                        <Typography variant="body2">{f.label}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>{f.count}</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* ── Time to Hire ── */}
          {tab === 1 && data?.timeToHire && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Overall</Typography>
                  {[
                    { label: 'Average', value: days(data.timeToHire.overall?.avgDays), color: '#6a1b9a' },
                    { label: 'Median',  value: days(data.timeToHire.overall?.medianDays), color: '#1565c0' },
                    { label: 'Fastest', value: days(data.timeToHire.overall?.minDays), color: '#2e7d32' },
                    { label: 'Slowest', value: days(data.timeToHire.overall?.maxDays), color: '#b71c1c' },
                  ].map(s => (
                    <Box key={s.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f5f5f5' }}>
                      <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                      <Typography variant="body2" fontWeight={700} color={s.color}>{s.value}</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Department</TableCell>
                        <TableCell align="right">Hired</TableCell>
                        <TableCell align="right">Avg Days</TableCell>
                        <TableCell align="right">Median</TableCell>
                        <TableCell align="right">Min</TableCell>
                        <TableCell align="right">Max</TableCell>
                        <TableCell>Bar</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.timeToHire.byDept?.length === 0 && (
                        <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No completed hires in period</TableCell></TableRow>
                      )}
                      {data.timeToHire.byDept?.map((d: any) => {
                        const maxDays = Math.max(...(data.timeToHire.byDept?.map((x: any) => parseInt(x.avgDays || 0)) ?? [1]), 1);
                        return (
                          <TableRow key={d.dept} hover>
                            <TableCell>{d.dept}</TableCell>
                            <TableCell align="right">{d.hired}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: d.avgDays > 60 ? '#b71c1c' : d.avgDays > 30 ? '#f57f17' : '#2e7d32' }}>{days(d.avgDays)}</TableCell>
                            <TableCell align="right">{days(d.medianDays)}</TableCell>
                            <TableCell align="right" sx={{ color: '#2e7d32' }}>{days(d.minDays)}</TableCell>
                            <TableCell align="right" sx={{ color: '#b71c1c' }}>{days(d.maxDays)}</TableCell>
                            <TableCell sx={{ minWidth: 100 }}>
                              <LinearProgress variant="determinate" value={Math.round((parseInt(d.avgDays) / maxDays) * 100)}
                                sx={{ height: 8, borderRadius: 4,
                                  '& .MuiLinearProgress-bar': { bgcolor: d.avgDays > 60 ? '#b71c1c' : d.avgDays > 30 ? '#f57f17' : '#2e7d32' } }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}

          {/* ── Source Effectiveness ── */}
          {tab === 2 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Source</TableCell>
                    <TableCell align="right">Applications</TableCell>
                    <TableCell align="right">Hired</TableCell>
                    <TableCell align="right">Hire Rate</TableCell>
                    <TableCell align="right">Avg Days to Hire</TableCell>
                    <TableCell>Quality Bar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.sourceEffectiveness ?? []).length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>No data for selected period</TableCell></TableRow>
                  )}
                  {(data?.sourceEffectiveness ?? []).map((s: any) => {
                    const cfg = SOURCE_CFG[s.source] ?? SOURCE_CFG.other;
                    const hireRate = parseFloat(s.hireRate || 0);
                    return (
                      <TableRow key={s.source} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>{cfg.emoji}</Typography>
                            <Typography variant="body2" fontWeight={600}>{cfg.label}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{s.applications}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color={s.hired > 0 ? '#2e7d32' : 'text.secondary'}>{s.hired}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={`${hireRate.toFixed(1)}%`}
                            sx={{ bgcolor: hireRate > 20 ? '#e8f5e9' : hireRate > 10 ? '#fffde7' : '#ffebee',
                              color:  hireRate > 20 ? '#2e7d32' : hireRate > 10 ? '#f57f17' : '#b71c1c', fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell align="right">{days(s.avgDaysToHire)}</TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <LinearProgress variant="determinate" value={Math.min(hireRate * 3, 100)}
                            sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: cfg.color } }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Offer Analysis ── */}
          {tab === 3 && data?.offerStats && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Timing</Typography>
                  {[
                    { label: 'Apply → Offer (avg)', value: days(data.offerStats.timing?.avgDaysApplyToOffer) },
                    { label: 'Offer → Accept (avg)', value: days(data.offerStats.timing?.avgDaysOfferToAccept) },
                  ].map(s => (
                    <Box key={s.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f5f5f5' }}>
                      <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                      <Typography variant="body2" fontWeight={700}>{s.value}</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Avg Salary</TableCell>
                        <TableCell>Bar</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.offerStats.byStatus ?? []).map((s: any) => {
                        const maxCount = Math.max(...(data.offerStats.byStatus?.map((x: any) => x.count) ?? [1]), 1);
                        const color = s.status === 'accepted' ? '#2e7d32' : s.status === 'declined' ? '#b71c1c' : '#546e7a';
                        return (
                          <TableRow key={s.status}>
                            <TableCell><Chip size="small" label={s.status} sx={{ bgcolor: color + '15', color, fontSize: '0.65rem' }} /></TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{s.count}</TableCell>
                            <TableCell align="right">{s.avgSalary ? `${s.currency ?? 'HKD'} ${parseInt(s.avgSalary).toLocaleString()}` : '—'}</TableCell>
                            <TableCell sx={{ minWidth: 120 }}>
                              <LinearProgress variant="determinate" value={Math.round((s.count / maxCount) * 100)}
                                sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: color } }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}

          {/* ── By Department ── */}
          {tab === 4 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Applications</TableCell>
                    <TableCell align="right">Hired</TableCell>
                    <TableCell align="right">Active</TableCell>
                    <TableCell align="right">Open Reqs</TableCell>
                    <TableCell align="right">Hire Rate</TableCell>
                    <TableCell>Pipeline</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.deptBreakdown ?? []).map((d: any) => {
                    const hireRate = parseFloat(d.hireRate || 0);
                    const maxApps = Math.max(...(data?.deptBreakdown ?? []).map((x: any) => parseInt(x.applications || 0)), 1);
                    return (
                      <TableRow key={d.dept} hover>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.dept}</Typography></TableCell>
                        <TableCell align="right">{d.applications}</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 700 }}>{d.hired}</TableCell>
                        <TableCell align="right" sx={{ color: '#1565c0' }}>{d.active}</TableCell>
                        <TableCell align="right">{d.openReqs}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={`${hireRate}%`}
                            sx={{ bgcolor: hireRate > 15 ? '#e8f5e9' : '#fff3e0',
                              color: hireRate > 15 ? '#2e7d32' : '#f57f17', fontSize: '0.65rem' }} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          <LinearProgress variant="determinate" value={Math.round((parseInt(d.applications) / maxApps) * 100)}
                            sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: '#6a1b9a' } }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Monthly Trend ── */}
          {tab === 5 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Applications</TableCell>
                    <TableCell align="right">Hired</TableCell>
                    <TableCell align="right">Rejected/Withdrawn</TableCell>
                    <TableCell>Hired Bar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.monthlyTrend ?? []).map((m: any) => {
                    const maxHired = Math.max(...(data?.monthlyTrend ?? []).map((x: any) => parseInt(x.hired || 0)), 1);
                    return (
                      <TableRow key={m.month} hover>
                        <TableCell><Typography variant="body2" fontWeight={600}>{m.month}</Typography></TableCell>
                        <TableCell align="right">{m.applications}</TableCell>
                        <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 700 }}>{m.hired}</TableCell>
                        <TableCell align="right" sx={{ color: '#b71c1c' }}>{m.rejected}</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <LinearProgress variant="determinate" value={Math.round((parseInt(m.hired || 0) / maxHired) * 100)}
                            sx={{ height: 10, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32' } }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Active Pipeline ── */}
          {tab === 6 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Requisition</TableCell>
                    <TableCell>Dept</TableCell>
                    <TableCell align="right">Days Open</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Applied</TableCell>
                    <TableCell align="right">Screening</TableCell>
                    <TableCell align="right">Interview</TableCell>
                    <TableCell align="right">Final</TableCell>
                    <TableCell align="right">Offer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pipeline.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary' }}>No open requisitions</TableCell></TableRow>
                  )}
                  {pipeline.map((r: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell><Typography variant="body2" fontWeight={600}>{r.reqTitle}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{r.dept}</Typography></TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={`${r.daysOpen}d`}
                          sx={{ bgcolor: r.daysOpen > 90 ? '#ffebee' : r.daysOpen > 45 ? '#fff3e0' : '#e8f5e9',
                            color: r.daysOpen > 90 ? '#b71c1c' : r.daysOpen > 45 ? '#e65100' : '#2e7d32', fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{r.total}</TableCell>
                      <TableCell align="right">{r.applied}</TableCell>
                      <TableCell align="right">{r.screening}</TableCell>
                      <TableCell align="right">{r.interviewing}</TableCell>
                      <TableCell align="right">{r.finalReview}</TableCell>
                      <TableCell align="right">{r.offer}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Interviews ── */}
          {tab === 7 && data?.interviewStats && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Overall Interview Metrics</Typography>
                  {[
                    { label: 'Total Interviews',    value: data.interviewStats.overall?.total },
                    { label: 'Completed',           value: data.interviewStats.overall?.completed },
                    { label: 'No-Show Rate',        value: pct(data.interviewStats.overall?.noShowRate) },
                    { label: 'Avg Rating',          value: parseFloat(data.interviewStats.overall?.avgRating || 0).toFixed(2) },
                    { label: 'Recommend Hire',      value: data.interviewStats.overall?.recommendHire },
                    { label: 'Recommend No Hire',   value: data.interviewStats.overall?.recommendNoHire },
                  ].map(s => (
                    <Box key={s.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f5f5f5' }}>
                      <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                      <Typography variant="body2" fontWeight={700}>{s.value ?? '—'}</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Interview Type</TableCell>
                        <TableCell align="right">Count</TableCell>
                        <TableCell align="right">Avg Rating</TableCell>
                        <TableCell align="right">Hire Rec Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.interviewStats.byType ?? []).map((t: any) => (
                        <TableRow key={t.type} hover>
                          <TableCell>{t.type ?? 'Unspecified'}</TableCell>
                          <TableCell align="right">{t.count}</TableCell>
                          <TableCell align="right">{t.avgRating ? parseFloat(t.avgRating).toFixed(2) : '—'}</TableCell>
                          <TableCell align="right">
                            {t.hireRecommendRate ? (
                              <Chip size="small" label={`${parseFloat(t.hireRecommendRate).toFixed(0)}%`}
                                sx={{ fontSize: '0.65rem', bgcolor: '#e8f5e9', color: '#2e7d32' }} />
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}
